import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Checklist } from '../../components/Checklist';
import { ORG_IDENTIFY_CHECKLIST } from '../../utils/constants';
import { Search, AlertTriangle, Zap, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Identify() {
  const breadcrumbs = [{
    label: 'Protocols',
    path: '/'
  }, {
    label: 'Organization Protocol',
    path: '/organization'
  }, {
    label: 'Assets & Risk Awareness'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-4048">
      <div className="container mx-auto px-4 py-12" data-id="element-4049">
        {/* Header */}
        <div className="mb-12" data-id="element-4050">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-4051">
            <Search className="h-3 w-3 mr-2" data-id="element-4052" />
            Domain 2 of 6
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4053">
            Assets & Risk Awareness
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4054">
            Identify and understand your critical assets, data, and risks to
            prioritize protection efforts effectively.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4055">
          <div className="lg:col-span-2 space-y-12" data-id="element-4056">
            {/* What This Domain Covers */}
            <section data-id="element-4057">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4058">
                What This Domain Covers
              </h2>
              <ul className="space-y-2 text-slate-300" data-id="element-4059">
                <li className="flex items-start" data-id="element-4060">
                  <span className="text-cyan-500 mr-2" data-id="element-4061">•</span>
                  Asset inventory (devices, accounts, applications, cloud
                  services)
                </li>
                <li className="flex items-start" data-id="element-4062">
                  <span className="text-cyan-500 mr-2" data-id="element-4063">•</span>
                  Data classification and mapping
                </li>
                <li className="flex items-start" data-id="element-4064">
                  <span className="text-cyan-500 mr-2" data-id="element-4065">•</span>
                  Risk assessment and prioritization
                </li>
                <li className="flex items-start" data-id="element-4066">
                  <span className="text-cyan-500 mr-2" data-id="element-4067">•</span>
                  Third-party and supplier inventory
                </li>
                <li className="flex items-start" data-id="element-4068">
                  <span className="text-cyan-500 mr-2" data-id="element-4069">•</span>
                  Threat scenario understanding
                </li>
              </ul>
            </section>

            {/* Minimum Baseline Controls */}
            <section data-id="element-4070">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4071">
                Minimum Baseline Controls
              </h2>
              <div className="space-y-3" data-id="element-4072">
                <ControlItem title="Create Device Inventory" description="List all laptops, desktops, servers, and mobile devices with owners and locations." data-id="element-4073" />
                <ControlItem title="Document Critical Accounts" description="Identify admin accounts, cloud service accounts, and financial system access." data-id="element-4074" />
                <ControlItem title="List Cloud Services in Use" description="Document all SaaS applications, cloud storage, and external services." data-id="element-4075" />
                <ControlItem title="Classify Your Data" description="Label data as Public, Internal, or Confidential based on sensitivity." data-id="element-4076" />
                <ControlItem title="Identify Critical Assets" description="Determine which systems, if compromised, would halt business operations." data-id="element-4077" />
                <ControlItem title="Document Key Vendors" description="List vendors with access to your systems or data." data-id="element-4078" />
              </div>
            </section>

            {/* Best Practice Controls */}
            <section data-id="element-4079">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4080">
                Best Practice Controls
              </h2>
              <div className="space-y-3" data-id="element-4081">
                <ControlItem title="Maintain Asset Register" description="Keep a centralized, up-to-date inventory with ownership, location, and criticality." data-id="element-4082" />
                <ControlItem title="Conduct Annual Risk Assessment" description="Evaluate likelihood and impact of key threats (ransomware, phishing, data breach)." data-id="element-4083" />
                <ControlItem title="Create Risk Register" description="Document top 10 risks with mitigation plans and owners." data-id="element-4084" />
                <ControlItem title="Map Data Flows" description="Understand where sensitive data is stored, processed, and transmitted." data-id="element-4085" />
                <ControlItem title="Assess Vendor Risk" description="Evaluate security posture of vendors with access to critical systems or data." data-id="element-4086" />
                <ControlItem title="Define Recovery Priorities" description="Rank systems by Recovery Time Objective (RTO) and Recovery Point Objective (RPO)." data-id="element-4087" />
                <ControlItem title="Review Dependencies" description="Identify single points of failure (key person, critical vendor, essential system)." data-id="element-4088" />
                <ControlItem title="Update Inventory Quarterly" description="Review and update asset inventory as systems and services change." data-id="element-4089" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4090">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4091">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" data-id="element-4092" />
                Common Mistakes
              </h2>
              <div className="space-y-3 text-slate-400" data-id="element-4093">
                <p className="flex items-start" data-id="element-4094">
                  <span className="text-red-500 mr-2" data-id="element-4095">✗</span>
                  Creating inventory once and never updating it (assets change
                  constantly)
                </p>
                <p className="flex items-start" data-id="element-4096">
                  <span className="text-red-500 mr-2" data-id="element-4097">✗</span>
                  Forgetting shadow IT (employees using unauthorized cloud
                  services)
                </p>
                <p className="flex items-start" data-id="element-4098">
                  <span className="text-red-500 mr-2" data-id="element-4099">✗</span>
                  Treating all risks equally (prioritize based on likelihood and
                  impact)
                </p>
                <p className="flex items-start" data-id="element-4100">
                  <span className="text-red-500 mr-2" data-id="element-4101">✗</span>
                  Ignoring vendor and supply chain risks (third parties are
                  attack vectors)
                </p>
                <p className="flex items-start" data-id="element-4102">
                  <span className="text-red-500 mr-2" data-id="element-4103">✗</span>
                  Over-classifying data (if everything is "confidential",
                  nothing is)
                </p>
              </div>
            </section>

            {/* Quick Wins */}
            <section data-id="element-4104">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4105">
                <Zap className="h-6 w-6 text-yellow-500 mr-3" data-id="element-4106" />
                Quick Wins (7 Days)
              </h2>
              <div className="space-y-3" data-id="element-4107">
                <QuickWin day="Day 1-2" action="Create spreadsheet listing all company devices and owners" data-id="element-4108" />
                <QuickWin day="Day 3-4" action="List all cloud services and SaaS applications in use" data-id="element-4109" />
                <QuickWin day="Day 5-6" action="Identify top 5 critical systems (what would stop business if down?)" data-id="element-4110" />
                <QuickWin day="Day 7" action="Document top 3 risks and assign owners" data-id="element-4111" />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4112">
            <Checklist id="org-identify" title="Implementation Checklist" items={ORG_IDENTIFY_CHECKLIST} data-id="element-4113" />

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4114">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4115">
                Download Templates
              </h3>
              <Link to="/downloads/organization-pack" data-id="element-4116">
                <Button variant="secondary" fullWidth className="mb-3" data-id="element-4117">
                  <Download className="h-4 w-4 mr-2" data-id="element-4118" />
                  Asset Inventory Template
                </Button>
              </Link>
              <Link to="/downloads/organization-pack" data-id="element-4119">
                <Button variant="secondary" fullWidth data-id="element-4120">
                  <Download className="h-4 w-4 mr-2" data-id="element-4121" />
                  Risk Register Template
                </Button>
              </Link>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4122">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4123">Next Steps</h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-4124">
                Now that you know your assets and risks, implement protections
                to reduce exposure.
              </p>
              <Link to="/organization/protect" data-id="element-4125">
                <Button variant="outline" fullWidth data-id="element-4126">
                  Next: Protection & Prevention
                  <ArrowRight className="h-4 w-4 ml-2" data-id="element-4127" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center border-t border-slate-800 pt-8" data-id="element-4128">
          <Link to="/organization/governance" data-id="element-4129">
            <Button variant="outline" data-id="element-4130">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-4131" />
              Previous: Governance
            </Button>
          </Link>
          <Link to="/organization/protect" data-id="element-4132">
            <Button data-id="element-4133">
              Next Domain
              <ArrowRight className="h-4 w-4 ml-2" data-id="element-4134" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>;
}
function ControlItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <Card className="p-4 bg-slate-900/50 border-slate-800" data-id="element-4135">
      <h4 className="font-bold text-slate-100 text-sm mb-1" data-id="element-4136">{title}</h4>
      <p className="text-slate-400 text-sm" data-id="element-4137">{description}</p>
    </Card>;
}
function QuickWin({
  day,
  action
}: {
  day: string;
  action: string;
}) {
  return <div className="flex items-start" data-id="element-4138">
      <span className="text-yellow-500 font-mono text-sm mr-3 mt-0.5" data-id="element-4139">
        {day}
      </span>
      <span className="text-slate-300 text-sm" data-id="element-4140">{action}</span>
    </div>;
}